import { Box, Grid, Link, Theme, Typography } from '@mui/material';
import { format } from 'date-fns';
import { AnalyticsContext, BlogDetailsPageSummary, ContentTypeRichTextContent, ImageAsset as TImageAsset } from '@/graphql/__generated/sdk';
import { IBlogAuthorProps } from '../BlogAuthorDetails';
import BlogDetailsImageAssetContainer from '../BlogDetailsImageAssetContainer';
import { BlogImage, IBlogDetailsPage } from '../BlogDetailsPage';
import { blogDetailsHeroBackLinkStyles, blogDetailsHeroImageWrapperStyles, blogDetailsHeroInfoStyles, blogDetailsHeroLeftStyles, blogDetailsHeroRightStyles, blogDetailsHeroTakeAwaysStyles, blogDetailsHeroWrapperStyles, heroContentBoxStyles } from './BlogDetailsHero.styles';
import BlogDetailsHeroAuthorList from './BlogDetailsHeroAuthorList';
import ContentfulButton from '@shared-components/components/ContentfulButton';
import ContentTypeRichText from '@shared-components/components/ContentTypeRichText';
import TAGSvgIcon from '@shared-components/components/TAGSvgIcon';
import { useAppContext } from '@shared-components/context';
import { ContentfulBackground, Maybe } from '@shared-components/types';
import { customEvent, resolveContentfulBgColor, translateTagColorNameForContentful } from '@utils';
export interface IBlogDetailsHeroProps {
  author?: IBlogAuthorProps;
  reviewer?: IBlogAuthorProps;
  date: string;
  timeToRead: number;
  summary: Maybe<BlogDetailsPageSummary>;
  blogImage: BlogImage;
  imageAsset?: Maybe<TImageAsset>;
  isSmallScreen: boolean;
  title: string;
  heroBackgroundColor?: IBlogDetailsPage['heroBackgroundColor'];
  theme: Theme;
  heroContentTitle?: IBlogDetailsPage['heroContentTitle'];
  heroContentCollection?: IBlogDetailsPage['heroContentCollection'];
  heroButtonCollection?: IBlogDetailsPage['heroButtonCollection'];
}
const analyticsClick = (context: Maybe<AnalyticsContext> | undefined) => {
  if (context) {
    customEvent('tagEvent', context, context.eventName);
  }
};
const BlogDetailsHeroMainLink = ({
  textLinkColor,
  blogRootPath
}: {
  textLinkColor: string;
  blogRootPath: string;
}) => <Link data-testid="blog_details_hero_mainlink" color={textLinkColor} href={blogRootPath} underline="hover" sx={blogDetailsHeroBackLinkStyles} data-sentry-element="Link" data-sentry-component="BlogDetailsHeroMainLink" data-sentry-source-file="BlogDetailsHero.tsx">
		<TAGSvgIcon icon="ChevronLeftFunc" size={24} style={{
    display: 'inline-block',
    transform: 'translate(0, -.15rem)',
    marginRight: '.25rem'
  }} data-sentry-element="TAGSvgIcon" data-sentry-source-file="BlogDetailsHero.tsx" />
		<Typography color="inherit" variant="bodySmallBook" data-sentry-element="Typography" data-sentry-source-file="BlogDetailsHero.tsx">
			Blog Main Page
		</Typography>
	</Link>;
export default function BlogDetailsHero({
  date,
  timeToRead,
  blogImage,
  imageAsset,
  title,
  summary,
  author,
  reviewer,
  heroBackgroundColor = 'white',
  theme,
  isSmallScreen,
  heroContentTitle,
  heroContentCollection,
  heroButtonCollection
}: Readonly<IBlogDetailsHeroProps>) {
  const {
    config
  } = useAppContext();
  const blogRootPath = config.features.blogs.root;
  const formattedDate = format(new Date(date), 'MMMM dd, yyyy');
  const isDark = heroBackgroundColor?.toLowerCase().includes('dark') || heroBackgroundColor?.toLowerCase() === 'saffron';
  const contentfulSafeBackgroundColor = translateTagColorNameForContentful(heroBackgroundColor || '') || '';
  const contentfulBackgroundColor = resolveContentfulBgColor(contentfulSafeBackgroundColor as ContentfulBackground, theme);
  const textColor = isDark ? 'text.light' : 'text.secondary';
  const textLinkColor = isDark ? 'text.light' : 'text.interactive';
  const eyebrowColor = isDark ? 'accent02.main' : 'text.primary';
  const hasButtons = !!heroButtonCollection?.items?.length;
  const hasSubContent = !!heroContentCollection?.items?.length;
  return <Grid data-testid="blog_details_hero" container justifyContent="flex-start" sx={blogDetailsHeroWrapperStyles(contentfulBackgroundColor)} data-sentry-element="Grid" data-sentry-component="BlogDetailsHero" data-sentry-source-file="BlogDetailsHero.tsx">
			<Grid data-testid="blog_details_hero_left" item xs={12} md={6} sx={blogDetailsHeroLeftStyles} data-sentry-element="Grid" data-sentry-source-file="BlogDetailsHero.tsx">
				<BlogDetailsHeroMainLink textLinkColor={textLinkColor} blogRootPath={blogRootPath} data-sentry-element="BlogDetailsHeroMainLink" data-sentry-source-file="BlogDetailsHero.tsx" />
				<Box sx={blogDetailsHeroInfoStyles} data-sentry-element="Box" data-sentry-source-file="BlogDetailsHero.tsx">
					<Typography color={eyebrowColor} variant="bodySmallBook" component={'span'} data-sentry-element="Typography" data-sentry-source-file="BlogDetailsHero.tsx">
						{timeToRead} min read
					</Typography>
					<Typography color={eyebrowColor} variant="bodySmallBook" component={'span'} data-sentry-element="Typography" data-sentry-source-file="BlogDetailsHero.tsx">
						|
					</Typography>
					<Typography data-testid="blog_details_date" color={eyebrowColor} variant="bodySmallBook" component={'span'} data-sentry-element="Typography" data-sentry-source-file="BlogDetailsHero.tsx">
						Published on: {formattedDate}
					</Typography>
				</Box>
				<Box marginBottom={'1.5rem'} data-sentry-element="Box" data-sentry-source-file="BlogDetailsHero.tsx">
					{title && title !== '' && <Typography component={'h1'} data-testid="blog_details_hero_title" color={textColor} variant="heroMedium">
							{title}
						</Typography>}
				</Box>
				<BlogDetailsHeroAuthorList author={author} reviewer={reviewer} textColor={textColor} theme={theme} data-sentry-element="BlogDetailsHeroAuthorList" data-sentry-source-file="BlogDetailsHero.tsx" />
				<Box sx={blogDetailsHeroTakeAwaysStyles} data-sentry-element="Box" data-sentry-source-file="BlogDetailsHero.tsx">
					<Typography color={textColor} variant="bodyLargeBook" sx={{
          marginBottom: '.5rem'
        }} component="p" data-sentry-element="Typography" data-sentry-source-file="BlogDetailsHero.tsx">
						KEY TAKEAWAYS
					</Typography>
					{summary?.json && <ContentTypeRichText content={summary as ContentTypeRichTextContent} contentMaxWidth="100%" disableGutters={true} disablePaddingOn={['Top', 'Bottom']} backgroundColor="transparent" docProps={{
          color: textColor
        }} />}
				</Box>
			</Grid>
			<Grid data-testid="blog_details_hero_right" item xs={12} md={6} sx={blogDetailsHeroRightStyles} data-sentry-element="Grid" data-sentry-source-file="BlogDetailsHero.tsx">
				<Box sx={blogDetailsHeroImageWrapperStyles} data-sentry-element="Box" data-sentry-source-file="BlogDetailsHero.tsx">
					<BlogDetailsImageAssetContainer blogImage={blogImage} imageAsset={imageAsset} usePreloader={true} isSmallScreen={isSmallScreen} data-sentry-element="BlogDetailsImageAssetContainer" data-sentry-source-file="BlogDetailsHero.tsx" />
				</Box>
				{heroContentTitle?.json && <ContentTypeRichText content={heroContentTitle as ContentTypeRichTextContent} disableGutters={true} backgroundColor={heroBackgroundColor} centerAlign={false} disablePaddingOn={['Top', 'Bottom']} />}
				{hasSubContent && <Box data-testid="blog_details_hero_content-list" sx={heroContentBoxStyles}>
						{heroContentCollection.items.map(item => item?.content?.json ? <ContentTypeRichText key={item._id} content={item.content} disableGutters={true} backgroundColor={heroBackgroundColor} centerAlign={false} disablePaddingOn={['Top', 'Bottom']} docProps={{
          variant: 'bodySmallBook'
        }} /> : null)}
					</Box>}
				{hasButtons && <Box data-testid="blog_details_hero_cta">
						{heroButtonCollection?.items?.map(button => button ? <ContentfulButton key={button.sys.id} {...button} dataTestId="service_line_hero_cta" onClick={() => analyticsClick(button.analyticsContext)} /> : null)}
					</Box>}
			</Grid>
		</Grid>;
}